import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Box, Card, Typography, Skeleton } from "@mui/material";
import api from "../../utils/api";

const COLOR_HEX = { error: "#dc2626", warning: "#b45309", info: "#2f63ad" };

const markerIcon = (hex) => new L.divIcon({
  html: `<div style="width:16px;height:16px;border-radius:50%;background:${hex};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>`,
  className: "",
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -8],
});
const MARKER_ICONS = Object.fromEntries(Object.entries(COLOR_HEX).map(([k, hex]) => [k, markerIcon(hex)]));

const MapBoundsController = ({ items }) => {
  const map = useMap();
  useEffect(() => {
    if (items.length === 0) return;
    map.fitBounds(L.latLngBounds(items.map((i) => [i.lat, i.lng])), { padding: [30, 30], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  return null;
};

// "Open items map" — every still-open Work Order / Violation / Architectural
// Request that has a geocoded property, clustered on a live map, with a
// category filter and a list of the oldest open items on the side.
const OpenItemsMap = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeKeys, setActiveKeys] = useState(null); // null = all active

  useEffect(() => {
    api.get("/dashboard/open-items-map").then((res) => setData(res.data)).catch(() => {});
  }, []);

  const visibleItems = useMemo(() => {
    if (!data) return [];
    if (!activeKeys) return data.items;
    return data.items.filter((i) => activeKeys.has(i.module));
  }, [data, activeKeys]);

  const toggleCategory = (key) => {
    setActiveKeys((prev) => {
      const all = new Set(data.categories.map((c) => c.key));
      const current = prev || all;
      const next = new Set(current);
      if (next.has(key) && next.size === 1) return all; // clicking the only active one resets to all
      if (next.has(key)) next.delete(key); else next.add(key);
      if (next.size === all.size) return null;
      return next;
    });
  };

  if (!data) {
    return (
      <Card sx={{ borderRadius: "16px", p: 2.5, mb: 3, border: "1px solid rgba(0,0,0,0.06)" }}>
        <Skeleton variant="text" width={200} />
        <Skeleton variant="rectangular" height={360} sx={{ borderRadius: "12px", mt: 1.5 }} />
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: "16px", p: 2.5, mb: 3, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>Open items map</Typography>
          <Typography variant="caption" sx={{ color: "#9aa5b4" }}>{data.total} open · {data.community}</Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {data.categories.map((c) => {
            const isActive = !activeKeys || activeKeys.has(c.key);
            return (
              <Box
                key={c.key}
                onClick={() => toggleCategory(c.key)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.5,
                  borderRadius: "999px", cursor: "pointer", border: "1px solid",
                  borderColor: isActive ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.06)",
                  backgroundColor: isActive ? "#fff" : "rgba(0,0,0,0.03)",
                  opacity: isActive ? 1 : 0.5,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLOR_HEX[c.color] }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#223142" }}>{c.label} · {c.count}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2.5, flexDirection: { xs: "column", md: "row" } }}>
        <Box sx={{
          flex: "2 1 0", minWidth: 0, height: { xs: 320, md: 420 }, borderRadius: "12px", overflow: "hidden",
          border: "1px solid #eef2f7",
          "& .leaflet-container": { height: "100%", width: "100%", fontFamily: "inherit" },
          "& .leaflet-popup-content-wrapper": { borderRadius: "10px" },
        }}>
          <MapContainer center={[28.18, -82.63]} zoom={13} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <MapBoundsController items={visibleItems} />
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={40}
              spiderfyOnMaxZoom
              showCoverageOnHover={false}
              iconCreateFunction={(cluster) => {
                const count = cluster.getChildCount();
                const size = count >= 20 ? 40 : count >= 10 ? 34 : 28;
                return L.divIcon({
                  html: `<div style="width:${size}px;height:${size}px;background:rgba(47,99,173,0.92);border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.25);font-size:${count >= 20 ? 13 : 12}px;font-weight:700;color:#fff;font-family:inherit;">${count}</div>`,
                  className: "",
                  iconSize: [size, size],
                  iconAnchor: [size / 2, size / 2],
                });
              }}
            >
              {visibleItems.map((item) => (
                <Marker key={item.id} position={[item.lat, item.lng]} icon={MARKER_ICONS[item.color] || MARKER_ICONS.info}>
                  <Popup>
                    <strong>{item.address}</strong>
                    <div style={{ marginTop: 4, color: "#6b778c", fontSize: "0.85rem" }}>
                      {item.label} · {item.code} · {item.daysOpen}d open
                    </div>
                    <a
                      href={item.link}
                      onClick={(e) => { e.preventDefault(); navigate(item.link); }}
                      style={{ display: "block", marginTop: 6, color: "#2f63ad", fontSize: "0.85rem", fontWeight: 600, textDecoration: "none" }}
                    >
                      View →
                    </a>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </Box>

        <Box sx={{ flex: "1 1 0", minWidth: { xs: "100%", md: 220 } }}>
          <Typography variant="caption" sx={{ color: "#9aa5b4", fontWeight: 700, letterSpacing: 0.5 }}>
            OLDEST OPEN ITEMS
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
            {visibleItems.slice(0, 6).map((item) => (
              <Box
                key={item.id}
                component={Link}
                to={item.link}
                sx={{
                  display: "flex", alignItems: "flex-start", gap: 1, py: 1,
                  borderBottom: "1px solid rgba(0,0,0,0.05)", textDecoration: "none",
                  borderRadius: "8px", mx: -1, px: 1,
                  "&:hover": { backgroundColor: "rgba(47,99,173,0.05)" },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLOR_HEX[item.color], mt: 0.6, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.25 }} noWrap>
                    {item.address?.split(",")[0]}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9aa5b4" }}>{item.label} · {item.code}</Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: COLOR_HEX[item.color], flexShrink: 0 }}>
                  {item.daysOpen}d
                </Typography>
              </Box>
            ))}
            {visibleItems.length === 0 && (
              <Typography variant="body2" sx={{ color: "#9aa5b4", mt: 1 }}>No open items to show.</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default OpenItemsMap;
